import { afterNextRender, DestroyRef, Directive, effect, ElementRef, inject, input, signal } from '@angular/core';

/** A contour system: rings ripple outward from (x, y), given in fractions of the canvas. */
export interface ContourCenter {
  x: number;
  y: number;
  /** Phases the ring's wobble, so no two systems come out the same shape. */
  seed: number;
  rings: number;
}

/** A dotted flight path, drawn as an arc between two fractional points. */
export type ContourRoute = readonly [readonly [number, number], readonly [number, number]];

export interface ContourMap {
  /** Pixels between rings. */
  step: number;
  centers: readonly ContourCenter[];
  routes?: readonly ContourRoute[];
  /** Ring colour as a bare "r,g,b" triplet — alpha is applied per ring. */
  tint?: string;
  alphaBase?: number;
  /** How much each successive ring fades, so systems dissolve outward instead of ending. */
  alphaFade?: number;
}

const DEFAULT_TINT = '164, 167, 249';
const DEFAULT_ALPHA_BASE = 0.2;
const DEFAULT_ALPHA_FADE = 0.022;
const ROUTE_TINT = 'rgba(135, 246, 206, 0.55)';
/** Segments per ring. 96 is smooth at hero size and still cheap. */
const RING_SEGMENTS = 96;
/** Rings are flattened vertically, so they read as terrain seen from above rather than as circles. */
const RING_SQUASH = 0.82;

/**
 * The map under the drenched surfaces — contour rings with dotted routes threaded between them.
 *
 * Decorative, and painted rather than drawn in the DOM: a few hundred hairline paths as SVG would
 * cost the same pixels and far more nodes. It carries no information, so it is `aria-hidden` at the
 * call site and never animates — the pins on top are what move.
 */
@Directive({
  selector: 'canvas[appContourMap]',
  standalone: true,
})
export class ContourMapDirective {
  readonly map = input.required<ContourMap>({ alias: 'appContourMap' });

  private readonly canvas = inject<ElementRef<HTMLCanvasElement>>(ElementRef).nativeElement;
  private readonly destroyRef = inject(DestroyRef);

  /** Nothing can be measured — so nothing can be drawn — until the canvas is in the layout. */
  private readonly isRendered = signal<boolean>(false);

  constructor() {
    afterNextRender(() => {
      this.isRendered.set(true);

      // The map is fluid, so it is repainted on resize. Where that cannot be observed (jsdom under
      // test), it is still painted once — the decoration is never load-bearing enough to throw over.
      if (typeof ResizeObserver === 'undefined') {
        this.draw();
        return;
      }

      // Fires once on observe, which is also the first paint.
      const observer = new ResizeObserver(() => this.draw());
      observer.observe(this.canvas);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });

    effect(() => {
      this.map();
      if (this.isRendered()) {
        this.draw();
      }
    });
  }

  private draw(): void {
    // No context, or no box to draw in: the map is decoration, so it is simply not painted.
    const { width, height } = this.canvas.getBoundingClientRect();
    const context = this.canvas.getContext('2d');
    if (!context || !width || !height) {
      return;
    }

    // Back the canvas at device resolution; hairlines go to mud at 1x on a retina screen.
    const ratio = window.devicePixelRatio || 1;
    this.canvas.width = width * ratio;
    this.canvas.height = height * ratio;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.clearRect(0, 0, width, height);

    const map = this.map();
    this.drawContours(context, map, width, height);
    this.drawRoutes(context, map, width, height);
  }

  private drawContours(context: CanvasRenderingContext2D, map: ContourMap, width: number, height: number): void {
    const tint = map.tint ?? DEFAULT_TINT;
    const alphaBase = map.alphaBase ?? DEFAULT_ALPHA_BASE;
    const alphaFade = map.alphaFade ?? DEFAULT_ALPHA_FADE;

    context.lineWidth = 1;

    for (const center of map.centers) {
      const cx = center.x * width;
      const cy = center.y * height;

      for (let ring = 1; ring <= center.rings; ring++) {
        const radius = ring * map.step;
        context.beginPath();

        for (let i = 0; i <= RING_SEGMENTS; i++) {
          const angle = (i / RING_SEGMENTS) * Math.PI * 2;
          // Three summed harmonics: enough to read as land, not enough to read as noise.
          const wobble =
            1 +
            0.16 * Math.sin(3 * angle + center.seed) +
            0.09 * Math.sin(7 * angle + center.seed * 2.3) +
            0.05 * Math.sin(11 * angle + center.seed * 4.1);
          const r = radius * wobble;
          const x = cx + Math.cos(angle) * r;
          const y = cy + Math.sin(angle) * r * RING_SQUASH;

          if (i === 0) {
            context.moveTo(x, y);
          } else {
            context.lineTo(x, y);
          }
        }

        context.closePath();
        context.strokeStyle = `rgba(${tint}, ${Math.max(0.02, alphaBase - ring * alphaFade)})`;
        context.stroke();
      }
    }
  }

  private drawRoutes(context: CanvasRenderingContext2D, map: ContourMap, width: number, height: number): void {
    for (const [from, to] of map.routes ?? []) {
      const x1 = from[0] * width;
      const y1 = from[1] * height;
      const x2 = to[0] * width;
      const y2 = to[1] * height;

      // The arc lifts with the distance it spans, the way a flight path does on a map.
      const controlX = (x1 + x2) / 2;
      const controlY = Math.min(y1, y2) - Math.abs(x2 - x1) * 0.22;

      context.beginPath();
      context.setLineDash([1.5, 6]);
      context.lineWidth = 1.6;
      context.strokeStyle = ROUTE_TINT;
      context.moveTo(x1, y1);
      context.quadraticCurveTo(controlX, controlY, x2, y2);
      context.stroke();
      context.setLineDash([]);
      context.lineWidth = 1;
    }
  }
}
